import { router, useLocalSearchParams } from "expo-router";
import { useState } from "react";
import { View } from "react-native";
import Animated, { FadeIn } from "react-native-reanimated";
import { useUnistyles } from "react-native-unistyles";

import Button from "$src/core/components/atoms/Button/Button";
import { AnimatedScrollView } from "$src/core/components/atoms/ScrollView/ScrollView";
import Spinner from "$src/core/components/atoms/Spinner/Spinner";
import Text from "$src/core/components/atoms/Text/Text";
import TextInput from "$src/core/components/atoms/TextInput/TextInput";
import TVFocusGuideView from "$src/core/components/atoms/TVFocusGuideView";
import ProfileSelectorButton from "$src/core/components/molecules/ProfileSelectorButton/ProfileSelectorButton";
import { TEST_IDS } from "$src/core/constants/test-ids";
import useProfiles from "$src/core/hooks/use-profiles";
import useStackHeader from "$src/core/hooks/use-stack-header";
import useTranslation from "$src/core/hooks/use-translation";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import Alerter from "$src/core/services/alerting";
import Logging from "$src/core/services/logging/logging";
import CustomPlatform from "$src/core/utils/platform/platform";

import $ from "./UpsertProfilePage.styles";

/* A text field grabbing focus on TV opens the on-screen keyboard over the form. */
const AUTO_FOCUS_NAME = CustomPlatform.select({ tv: false, default: true });

const UpsertProfilePage: React.FC = () => {
  useStackHeader({
    left: "back",
  });

  const { t } = useTranslation();
  const { theme } = useUnistyles();
  const { tints } = theme.profile;
  const { id } = useLocalSearchParams<{ id?: string }>();
  const isEdit = Boolean(id);

  const {
    profiles,
    status,
    error,
    refetch,
    addProfile,
    updateProfile,
    deleteProfile,
    isAdding,
    isUpdating,
    isDeleting,
  } = useProfiles();
  const profile = profiles?.find((candidate) => candidate.id === id);

  const [enteredName, setEnteredName] = useState<string | null>(null);
  const [pickedTintIndex, setPickedTintIndex] = useState<number | null>(null);

  const name = enteredName ?? profile?.name ?? "";
  // -1 when editing a profile whose tint has since left the palette: keep that
  // tint on show, so saving an unrelated edit does not recolour the profile.
  const tintIndex =
    pickedTintIndex ?? (profile ? tints.indexOf(profile.tint) : 0);
  const tint = tintIndex >= 0 ? tints[tintIndex] : (profile?.tint ?? tints[0]);
  const stepFrom = Math.max(tintIndex, 0);

  const isBusy = isAdding || isUpdating || isDeleting;
  const canSave = name.trim().length > 0;

  const stepTint = (step: number) =>
    setPickedTintIndex((stepFrom + step + tints.length) % tints.length);

  const handleSave = async () => {
    // Also reached from the keyboard's done key, which has no disabled state.
    if (!canSave || isBusy) return;

    try {
      if (id) {
        await updateProfile(id, name, tint);
      } else {
        await addProfile(name, tint);
      }
      if (router.canGoBack()) {
        router.back();
      } else {
        router.replace("/profiles");
      }
    } catch (saveError) {
      Logging.error(
        id ? "Could not update profile" : "Could not create profile",
        { error: saveError },
      );
      Alerter.error(t(id ? "ERROR/PROFILE_UPDATE" : "ERROR/PROFILE_CREATE"));
    }
  };

  const handleDelete = () => {
    if (!profile || isBusy) return;

    Alerter.prompt(t("PROFILE/EDIT/DELETE_CONFIRM", { name: profile.name }), {
      confirmLabel: t("PROFILE/EDIT/DELETE"),
      cancelLabel: t("COMMON/CANCEL"),
      onConfirm: () => {
        deleteProfile(profile)
          .then(() => {
            if (router.canGoBack()) {
              router.back();
            } else {
              router.replace("/profiles");
            }
          })
          .catch((deleteError: unknown) => {
            Logging.error("Could not delete profile", { error: deleteError });
            Alerter.error(t("ERROR/PROFILE_DELETE"));
          });
      },
    });
  };

  if (isEdit && status === "pending") {
    return <Spinner />;
  }

  if (isEdit && (status === "error" || !profile)) {
    return (
      <ErrorPage
        title={t("ERROR/PROFILES_PAGE/TITLE")}
        subtitle={t("ERROR/PROFILES_PAGE/SUBTITLE")}
        ctaText={t("COMMON/RETRY")}
        ctaIcon="retry"
        onPressCta={refetch}
        error={error}
      />
    );
  }

  return (
    <AnimatedScrollView
      contentContainerStyle={$.container}
      scrollEventThrottle={16}
    >
      <Animated.View style={$.step} entering={FadeIn.duration(100)}>
        <Text preset="heading1" accessibilityRole="header">
          {t(isEdit ? "PROFILE/EDIT/TITLE" : "PROFILE/CREATE/TITLE")}
        </Text>

        <Text preset="heading5" accessibilityRole="text" style={$.subtitle}>
          {t("PROFILE/UPSERT/SUBTITLE")}
        </Text>

        <TVFocusGuideView style={$.tintPicker} autoFocus>
          <Button
            name="profile-tint-previous"
            preset="transparent"
            icon="chevron-left"
            isRounded
            accessibilityLabel={t("COMMON/PREVIOUS/CTA/LABEL")}
            onPress={() => stepTint(-1)}
            testID={TEST_IDS.profile.tintPrevious}
          />

          <ProfileSelectorButton
            tint={tint}
            accessibilityLabel={t(
              "PROFILE/CREATE/COLOUR/OPTION_ACCESSIBILITY_LABEL",
              { index: stepFrom + 1 },
            )}
            onPress={() => stepTint(1)}
            testID={TEST_IDS.profile.tintPreview}
          />

          <Button
            name="profile-tint-next"
            preset="transparent"
            icon="chevron-right"
            isRounded
            accessibilityLabel={t("COMMON/NEXT/CTA/LABEL")}
            onPress={() => stepTint(1)}
            testID={TEST_IDS.profile.tintNext}
          />
        </TVFocusGuideView>

        <View style={$.field}>
          <TextInput
            value={name}
            autoCapitalize="words"
            autoFocus={AUTO_FOCUS_NAME}
            placeholder={t("PROFILE/CREATE/NAME/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("PROFILE/CREATE/NAME/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={setEnteredName}
            onSubmitEditing={handleSave}
            returnKeyType="done"
            textAlign="center"
            testID={TEST_IDS.profile.nameInput}
            style={$.input}
          />
        </View>

        <View style={$.buttons}>
          <Button
            preset="primary"
            name="profile-upsert-save"
            label={t("PROFILE/CREATE/SAVE")}
            isDisabled={!canSave || isBusy}
            isLoading={isAdding || isUpdating}
            onPress={handleSave}
            testID={TEST_IDS.profile.saveButton}
          />
          {isEdit && !profile?.isMain && (
            <Button
              preset="transparent"
              name="profile-upsert-delete"
              label={t("PROFILE/EDIT/DELETE")}
              isDisabled={isBusy}
              isLoading={isDeleting}
              onPress={handleDelete}
              testID={TEST_IDS.profile.deleteButton}
            />
          )}
        </View>
      </Animated.View>
    </AnimatedScrollView>
  );
};

export default UpsertProfilePage;
